import { ReadonlyVec3, vec3 } from 'gl-matrix';
import type { RoiId } from '~/domain/changes/ids';
import type { PlaneMm } from '~/domain/geometry/patientGeometry';
import {
    ContourGeometricType,
    DicomContour,
} from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    RoiCreatedEvent,
    RoiPropertiesUpdatedEvent,
} from '~/domain/events';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import {
    countClearedSlicesWithContent,
    planKeepEveryNthSliceClears,
} from './deleteScopePlan';
import { isImageSlicePlane, resolveTextureSlice } from './planeSliceResolution';
import { StructureSet } from '../../../domain/patient/structureset';
import { EnvironmentObject } from '../common/EnvironmentObject';
import { ImageObject } from '../image/ImageObject';
import { RegionOfInterest, RoiIntensityStats } from './RegionOfInterest';
import { EventSubscriptions } from '~/domain/events/EventSubscriptions';
import { Grid } from '../common/Grid';
import { ViewManager } from '~/engine/view/ViewManager';

/**
 * {@link StructureSetObject} represents a collection of {@link RegionOfInterest RegionsOfInterest}.\
 * It inherits from {@link EnvironmentObject} so it has a physical appearance in the PatientSpace.
 */
export class StructureSetObject extends EnvironmentObject {
    /**
     * TODO: Is this a good idea? Atleast it makes things easy.
     *
     */
    public readonly parent: StructureSet;

    /**
     * An {@link Array} of {@link RegionOfInterest RegionsOfInterest} stored within this {@link StructureSetObject}.
     */
    private regionsOfInterest: Map<RoiId, RegionOfInterest>;
    private _eventSubscriptions?: EventSubscriptions;

    private _selectedStructure: RegionOfInterest | undefined = undefined;
    public get selectedStructure(): RegionOfInterest | undefined {
        return this._selectedStructure;
    }

    public set selectedStructure(roi: RegionOfInterest | undefined) {
        if (!roi) {
            this._selectedStructure = undefined;
            return;
        }
        for (const roiSs of this.structures) {
            // verify that roi is in this structure set
            if (roi === roiSs) {
                this._selectedStructure = roi;
                return;
            }
        }
        throw new Error(
            `Attempt to set selectedStructure to '${roi.name}', but it is not part of this StructureSet`
        );
    }

    /**
     * Method to construct {@link StructureSetObject} directly from a {@link StructureSet}-datamodel, which is DICOM datamodel.
     * @param structureSet The DICOM datamodel from which the {@link StructureSetObject} is constructed.
     */
    public constructor(structureSet: StructureSet) {
        super();
        this.parent = structureSet;
        if (!structureSet.image) {
            throw new Error('StructureSetObject must have an image');
        }
        this.regionsOfInterest = new Map<RoiId, RegionOfInterest>();
        for (const roi of structureSet) {
            const regionOfInterest = new RegionOfInterest(
                roi,
                this.grid,
                this.parent.dicomUid
            );
            this.regionsOfInterest.set(
                regionOfInterest.roiId,
                regionOfInterest
            );
        }

        const subscriptions = [
            domainEventBus.subscribe(
                RoiPropertiesUpdatedEvent.EVENT_TYPE,
                this.handleRoiPropertiesUpdated.bind(this)
            ),
            domainEventBus.subscribe(
                RoiCreatedEvent.EVENT_TYPE,
                this.handleRoiCreated.bind(this)
            ),
        ];

        this._eventSubscriptions = new EventSubscriptions(subscriptions);
    }

    public get displayName(): string {
        return this.parent.structureSetLabel;
    }

    public get sopInstanceUID(): string {
        return this.parent.dicomUid;
    }

    public get structures(): RegionOfInterest[] {
        return Array.from(this.regionsOfInterest.values());
    }

    /**
     * Ensure contour edits to WebGL models are updated to the domain and DICOM datamodels.
     */
    public saveChanges(): void {
        const structureSet = this.parent;
        if (structureSet.image) {
            const image3d = structureSet.image;
            for (const roi of this.structures) {
                // Point contours are managed via
                // RoiPointCoordinatesChangeRequestedEvent
                if (roi.isPointRoi) {
                    continue;
                }
                if (roi.hasBeenModified) {
                    const roiContours: DicomContour[] = [];
                    const contourSequence = roi.sequence;
                    for (const slice of contourSequence.contourSlices) {
                        const imageUid = image3d.getUidForSlice(
                            slice.normalPosition
                        );
                        if (!imageUid) {
                            const point = image3d.grid.center;
                            point[2] = slice.normalPosition;
                            if (
                                image3d.grid
                                    .toAxisAlignedBoundingBox()
                                    .isPointInside(point)
                            ) {
                                throw new Error(
                                    `Image UID not found for slice z: ${slice.normalPosition} mm.`
                                );
                            } else {
                                console.warn(
                                    `Contour outside image z: ${slice.normalPosition} mm. Skipping.`
                                );
                            }
                            continue;
                        }
                        for (const contour of slice.contours) {
                            const dicomContour = new DicomContour(
                                ContourGeometricType.CLOSED_PLANAR,
                                contour.vertices,
                                structureSet.dicomStructureSet.referencedSopClassUid,
                                imageUid
                            );
                            roiContours.push(dicomContour);
                        }
                    }
                    const domainRoi = structureSet.getDicomRoi(roi.roiId);
                    if (domainRoi) {
                        domainRoi.contours = roiContours;
                    }
                    structureSet.dicomStructureSet.updateContourSequence(
                        roi.roiId
                    );
                }
            }
        }
    }

    public clearAllContours(roiId: RoiId): void {
        for (const roi of this.structures) {
            if (roi.roiId === roiId) {
                if (roi.isPointRoi) {
                    continue;
                }
                const signedDistanceField = roi.texture?.signedDistanceField;
                if (!signedDistanceField) {
                    continue;
                }
                // An ROI holding no contour has nothing to clear. Counted
                // through the same helper a caller states "this would not
                // delete anything" from, so the report and the write agree
                // by construction, and an allocated-but-empty SDF banks no
                // undo entry for a change that never happened.
                const clearedSliceCount = countClearedSlicesWithContent(
                    signedDistanceField,
                    { kind: 'whole-texture' }
                );
                if (clearedSliceCount === 0) {
                    continue;
                }
                const roiModify = roi.startModify('clearAllContours');
                signedDistanceField.clearTexture();
                roiModify.finish();
            }
        }
    }

    /**
     * Clears the contours of one ROI on the image slice cut by `plane`.
     *
     * @throws if `plane` coincides with no image slice plane (a rotated
     *   view, or a cut across an obliquely acquired grid): no single slice
     *   is then "the viewed one", and clearing any would clear the wrong
     *   contours.
     */
    public clearContoursOnSlice(
        roiId: RoiId,
        plane: PlaneMm,
        imgObject: ImageObject | undefined
    ): void {
        if (!imgObject) {
            throw new Error('ImageObject is undefined');
        }

        if (!isImageSlicePlane(imgObject, plane)) {
            throw new Error(
                'clearContoursOnSlice: the viewed plane is not an image slice plane'
            );
        }

        for (const roi of this.structures) {
            if (roi.roiId === roiId) {
                if (roi.isPointRoi) {
                    continue;
                }
                if (!roi.texture) {
                    throw new Error('ROI texture is undefined');
                }
                // Resolved through the same helper the panel's preview
                // resolves its plan from, so the slice cleared is the slice
                // the overlay pictured. A plane outside this ROI's own
                // texture addresses nothing here and is a no-op.
                const resolved = resolveTextureSlice(
                    roi.texture,
                    imgObject,
                    plane
                );
                if (resolved.kind !== 'slice') {
                    continue;
                }
                const textureSliceZ = resolved.sliceZ;
                const signedDistanceField = roi.texture.signedDistanceField;
                if (!signedDistanceField) {
                    continue;
                }
                // A slice that holds no contour has nothing to clear, and
                // must bank no undo entry for a change that never happened.
                const clearedSliceCount = countClearedSlicesWithContent(
                    signedDistanceField,
                    { kind: 'slices', slices: [textureSliceZ] }
                );
                if (clearedSliceCount === 0) {
                    continue;
                }
                const roiModify = roi.startModify('clearContoursOnSlice');
                signedDistanceField.clearSlice(textureSliceZ);
                roiModify.finish();
            }
        }
    }

    public keepEveryNthSlice(
        roiId: RoiId,
        keepEveryNthSliceValue: number
    ): EventHandlerResult {
        const roi = this.getRoi(roiId);
        if (roi?.isPointRoi) {
            throw new Error(
                'keepEveryNthSlice is not applicable to point ROIs'
            );
        }
        const texture = roi?.texture;
        const signedDistanceField = texture?.signedDistanceField;
        if (!texture || !signedDistanceField) {
            throw new Error('Texture or signedDistanceField is undefined');
        }

        // Planned before the modify session opens: an ROI with no content,
        // or a cadence that reaches no slice holding contour, must not bank
        // an undo entry for an edit that never happened. The planner also
        // validates the cadence, so an invalid value throws before anything
        // is opened.
        //
        // The plan lists every off-cadence slice, empty ones included — a
        // structure drawn on slices 10 and 20 only, thinned with N = 2,
        // plans every odd slice and would clear nothing — so the length of
        // the plan is not a guard. The emptiness count is.
        const slicesToClear = planKeepEveryNthSliceClears(
            signedDistanceField,
            keepEveryNthSliceValue
        );
        if (
            slicesToClear === undefined ||
            slicesToClear.length === 0 ||
            countClearedSlicesWithContent(signedDistanceField, {
                kind: 'slices',
                slices: slicesToClear,
            }) === 0
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                false
            );
        }

        // Once opened, the session is finished via try/finally on every exit
        // path, so an unexpected throw cannot leave the ROI stuck modifying.
        const roiModify = roi?.startModify('keepEveryNthSlice');
        try {
            slicesToClear.forEach((contourSliceZ) => {
                signedDistanceField.clearSlice(contourSliceZ);
            });
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true,
                {
                    layers: LayerBit.CONTOUR_LAYER_BIT,
                    volume: texture.contentBoundingBox,
                }
            );
        } finally {
            roiModify?.finish();
        }
    }

    public getRoi(roiId: RoiId): RegionOfInterest | undefined {
        return this.regionsOfInterest.get(roiId);
    }

    /**
     * Mean / SD / min / max of the image intensities under an ROI's mask,
     * bundled with the image so the display can label the unit (HU / SUV /
     * bare number) from its header.
     *
     * Read from the series this structure set is referenced to, never from
     * whichever image happens to be primary: the contours were drawn on that
     * series, and it is its header that gives the figures their scale.
     */
    public getRoiIntensityStatistics(
        roiId: RoiId
    ): RoiIntensityStats | undefined {
        const referencedImage = this.parent.image;
        if (!referencedImage) {
            return undefined;
        }
        return this.getRoi(roiId)?.getIntensityStatistics(referencedImage);
    }

    /**
     * The visible ROI whose contour EDGE lies nearest `patientCoords`, or
     * undefined when none is within the threshold.
     *
     * Edge proximity, not containment: `distanceToContour` returns the
     * absolute deviation from the iso value, so a point deep inside a
     * large structure is far from its edge and does not match. That is
     * what makes this the right question for "which curve am I pointing
     * at", and the wrong one for "which structure am I inside".
     *
     * @param patientCoords the point to measure from
     * @param thresholdMm how near the edge must be, in MILLIMETRES. The
     *   field is encoded with the iso at {@link ContourTexture.SDF_THRESHOLD}
     *   over each texture's OWN margin, so one byte is worth a different
     *   number of millimetres per ROI; the radius is converted per ROI so it
     *   means the same distance on every structure.
     * @param skip a ROI to pass over, such as one the views are not drawing
     */
    public findRoiWithinMm(
        patientCoords: ReadonlyVec3,
        thresholdMm: number,
        skip?: RegionOfInterest
    ): RegionOfInterest | undefined {
        return this.findRoiInternal(patientCoords, skip, {
            pointMm: thresholdMm,
            contourBytesFor: (roi) =>
                roi.texture
                    ? thresholdMm * roi.texture.sdfBytesPerMm()
                    : undefined,
        });
    }

    /**
     * The two branches below measure in different units — a point ROI in
     * millimetres from the point, a contour ROI in encoded bytes from the
     * iso — so each takes its own radius rather than one number standing
     * for both.
     *
     * (`minDist` is still compared ACROSS the two, which mixes those
     * units when a point ROI and a contour ROI are both in range. That is
     * how this has always chosen between them; it is not made worse here,
     * and it only decides which of two already-matching ROIs is named.)
     */
    private findRoiInternal(
        patientCoords: ReadonlyVec3,
        skip: RegionOfInterest | undefined,
        thresholds: {
            /** Match radius for a point ROI, in millimetres. */
            pointMm: number;
            /** Match radius for a contour ROI, in encoded SDF bytes. */
            contourBytesFor: (roi: RegionOfInterest) => number | undefined;
        }
    ): RegionOfInterest | undefined {
        const rois = Array.from(this.regionsOfInterest.values());
        const visibleRois = rois.filter((roi) => roi.isVisible && roi !== skip);
        let minDist = 9999;
        let result: RegionOfInterest | undefined = undefined;
        visibleRois.forEach((roi: RegionOfInterest) => {
            if (roi.isPointRoi) {
                const pt = roi.pointCoordinatesMm;
                if (pt) {
                    const dist = vec3.distance(patientCoords, pt);
                    if (dist < thresholds.pointMm && dist < minDist) {
                        result = roi;
                        minDist = dist;
                    }
                }
                return;
            }
            const texture = roi.texture;
            const sdf = texture?.signedDistanceField;
            const contourThreshold = thresholds.contourBytesFor(roi);
            if (sdf && contourThreshold !== undefined) {
                const sdfCoords =
                    texture.textureGrid.toVoxelSpace(patientCoords);
                const dist = sdf.distanceToContour(sdfCoords);

                if (dist < contourThreshold && dist < minDist) {
                    result = roi;
                    minDist = dist;
                }
            }
        });

        return result;
    }

    public get grid(): Grid {
        if (!this.parent.image) {
            throw new Error('StructureSetObject must have an image');
        }
        return this.parent.image.grid;
    }

    /**
     * Handle ROI property updates from domain events.
     * This specifically handles ROI collection-level operations.
     */
    private handleRoiPropertiesUpdated(event: RoiPropertiesUpdatedEvent): void {
        const properties = event.updatedProperties;

        // Handle ROI deletion
        if (properties.deleted === true) {
            this.deleteRoi(event.roiId);
        }

        // TODO: Handle all ROI property updates (now in RegionOfInterest)
    }

    private handleRoiCreated(event: RoiCreatedEvent): void {
        const { structureSetUid, roiId } = event;

        if (structureSetUid === this.parent.dicomUid) {
            const roi = this.parent.getDicomRoi(roiId);
            if (!roi) {
                throw new Error(
                    'RoiCreatedEvent received for a structure its structure set does not hold'
                );
            }

            const regionOfInterest = new RegionOfInterest(
                roi,
                this.grid,
                this.parent.dicomUid
            );
            this.regionsOfInterest.set(
                regionOfInterest.roiId,
                regionOfInterest
            );

            ViewManager.invalidate(
                LayerBit.CONTOUR_LAYER_BIT,
                regionOfInterest.boundingBox
            );
        }
    }

    /**
     * Method to erase the array of {@link roi RegionOfInterest}.
     */
    public delete() {
        for (const roi of this.structures) {
            roi.delete();
        }
        this.regionsOfInterest.clear();
        this._eventSubscriptions?.unsubscribeAll();
    }

    private deleteRoi(roiId: RoiId): void {
        const roi = this.getRoi(roiId);
        if (roi) {
            roi.delete();
            this.regionsOfInterest.delete(roiId);
            // Repaint so the deletion reaches the screen. Without this it
            // only did if the deleted ROI's own property-update handler
            // happened to run before ours and requested a repaint — true
            // for ROIs subscribed at load, false for ROIs created
            // afterwards (their subscription follows ours, and delete()
            // above removes it mid-dispatch), which left deleted
            // structures rendered until an unrelated interaction redrew
            // the view.
            //
            // Deliberately no culling volume. `roi.boundingBox` comes from
            // the SDF texture, and the texture getter rasterises one on
            // the GPU when none exists, so an ROI that was never drawn
            // (hidden, off-screen, a rolled-back stub) would be rasterised
            // only to be freed. The contour sequence's bounds are not a
            // substitute: for a painted ROI they lag the texture until it
            // is read back, and a too-small volume is exactly the "still
            // drawn" failure this repaint exists to prevent. An unculled
            // contour-layer repaint per view, coalesced per frame across a
            // bulk delete, is the cheaper and the safe choice.
            ViewManager.invalidate(LayerBit.CONTOUR_LAYER_BIT);
        }
    }
}
